import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminEvents, deleteEvent, adminLogout, adminVerify } from '../../services/adminApi';
import type { Event } from '../../types';
import './Admin.css';

export default function AdminDashboard() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
    // Verify auth on mount
    adminVerify().catch(() => {
      navigate('/admin/login');
    });
  }, [navigate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await getAdminEvents();
      setEvents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      if (err instanceof Error && err.message === 'Authentication required') {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also delete all questions and responses.`)) {
      return;
    }

    try {
      await deleteEvent(id);
      await loadEvents();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete event');
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-layout">
          <div className="loading">Loading events...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-layout">
        <div className="admin-header">
          <h1 className="admin-header__title">Surveys Administration</h1>
          <div className="admin-header__actions">
            <button onClick={() => navigate('/admin/events/new')} className="btn btn--primary">
              New Event
            </button>
            <button onClick={() => navigate('/admin/reports')} className="btn btn--secondary">
              Reports
            </button>
            <button onClick={handleLogout} className="btn btn--ghost">
              Logout
            </button>
          </div>
        </div>

        {error && <div className="admin-form__error">{error}</div>}

        <div className="admin-section">
          <h2 className="admin-section__title">Events</h2>
          {events.length === 0 ? (
            <p style={{ opacity: 0.8 }}>No events yet. Create your first event to get started.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table__header">Name</th>
                  <th className="admin-table__header">Slug</th>
                  <th className="admin-table__header">Date</th>
                  <th className="admin-table__header">Status</th>
                  <th className="admin-table__header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="admin-table__row">
                    <td className="admin-table__cell">{event.name}</td>
                    <td className="admin-table__cell">
                      <code style={{ fontSize: '0.875rem', opacity: 0.8 }}>{event.slug}</code>
                    </td>
                    <td className="admin-table__cell">{formatDate(event.event_date)}</td>
                    <td className="admin-table__cell">
                      <span
                        className={`admin-badge ${
                          event.is_active ? 'admin-badge--active' : 'admin-badge--inactive'
                        }`}
                      >
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="admin-table__cell">
                      <div className="admin-actions">
                        <button
                          onClick={() => navigate(`/admin/events/${event.id}`)}
                          className="btn btn--secondary btn--small"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/admin/reports/${event.id}`)}
                          className="btn btn--secondary btn--small"
                        >
                          Report
                        </button>
                        <button
                          onClick={() => handleDelete(event.id, event.name)}
                          className="btn btn--danger btn--small"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
